import { usersApi } from '@/features/users/api';
import type { CurrentUser } from '@/shared/types/user';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const saveUserToStorage = (user: CurrentUser | null) => {
  if (user) {
    localStorage.setItem('currentUser', JSON.stringify(user));
  } else {
    localStorage.removeItem('currentUser');
  }
};

interface UsersState {
  currentUser: CurrentUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  currentUser: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const fetchCurrentUser = createAsyncThunk('users/fetchCurrentUser', async () => {
  const user = await usersApi.getCurrentUser();
  return user;
});

export const logoutUser = createAsyncThunk('users/logout', async () => {
  await usersApi.logout();
});

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentUser: (state, action: PayloadAction<CurrentUser>) => {
      state.currentUser = action.payload;
      state.isAuthenticated = true;
      saveUserToStorage(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.currentUser = action.payload;
        state.isAuthenticated = true;
        saveUserToStorage(action.payload);
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.currentUser = null;
        state.isAuthenticated = false;
        state.error = action.error.message || 'Failed to fetch user';
        saveUserToStorage(null);
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.currentUser = null;
        state.isAuthenticated = false;
        saveUserToStorage(null);
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to logout';
      });
  },
});

export const { clearError, setCurrentUser } = usersSlice.actions;
const usersReducer = usersSlice.reducer;

export { usersReducer };
